import * as services from '@api';
import { requiredValidate, getHoverRow } from '../utils';
import { parseMobxStr } from '../../utils'
import { goBack } from '../navigate';
import { batchDo } from './batchDo';
import { PageStatusType } from '@stores/pagestore';
import { add } from '.';
import { submit } from '../workflow';
import { isType } from '@src/utils';

/**
 * 保存/新增
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 必填字段[树卡]
 *  treeStoreKey: string， 默认值：treeStore
 *  // 选填字段
 *  isGoBackAfterSave: boolean，默认值：true
 *  dirtyCheck: string，默认：IGNORE_UNCHANGED_TABLE_ROW
 *  targetStoreKey: string，默认值：cardStore
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeSaveHook: 保存请求前钩子
 *  afterSaveHook: 保存请求后钩子
 *  validateHook: 校验钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const save = {
  get visible() {
    const { cardStoreKey = 'cardStore', targetStoreKey = cardStoreKey } = this.params;
    const cardStore = this.rootStore.get(cardStoreKey);
    const targetStore = this.rootStore.get(targetStoreKey);
    if (!cardStore || !targetStore) return false;
    if (targetStore === cardStore) {
      return cardStore?.isEdit;
    } else {
      return targetStore?.uiState === PageStatusType.EDIT || targetStore?.uiState === PageStatusType.ADD;
    }
  },
  get enable() {
    return true;
  },
  beforeSaveHook(rootStore: any, data: any) {
    const { treeStoreKey = 'treeStore', cardStoreKey = 'cardStore' } = this.params;
    // const treeStore = rootStore.get(treeStoreKey);
    const cardStore = rootStore.get(cardStoreKey);
    // if (treeStore) {
    //   if (cardStore.uiState == PageStatusType.ADD) {
    //     data.data.data.parent = treeStore?.selectedKeys?.[0]
    //   }
    // }
    const { pageStore = {} } = rootStore;
    const { params = {} } = pageStore;
    if (params.parentId) {
      if (cardStore.uiState == PageStatusType.ADD) {
        data.data.data.parent = params.parentId
      }
    }
    return data;
  },
  afterSaveHook(rootStore: any, data: any) {
    const { treeStoreKey = 'treeStore', cardStoreKey = 'cardStore' } = this.params;
    const treeStore = rootStore.get(treeStoreKey);
    const cardStore = rootStore.get(cardStoreKey);
    if (treeStore) {
      // 数据回写
      if (cardStore.uiState == PageStatusType.ADD || cardStore.uiState == PageStatusType.EDIT) {
        rootStore.pageStore.bNeedRefresh = true
        treeStore.onSelect([data.data.id]);
      }
    }
  },
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['cardStoreKey']);
    const { dataSourceAlias = '', cardStoreKey = 'cardStore', treeStoreKey = 'treeStore', targetStoreKey = cardStoreKey, dirtyCheck, isGoBackAfterSave } = options;
    const { utils = {}, pageStore = {} } = rootStore;
    const { params = {}, query = {} } = pageStore;

    const cardStore = rootStore.get(cardStoreKey);
    const targetStore = rootStore.get(targetStoreKey);
    const treeStore = rootStore.get(treeStoreKey);
    if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
    if (!targetStore) throw (new Error(`The Store[${targetStoreKey}] not found`));

    try {
      let data = cardStore.getValue({ dirtyCheck });
      pageStore?.startLoading?.();

      // 数据校验
      const innerValidate = () => {
        if (!cardStore.validate()) {
          this.abort({ log: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8002D', '表单校验失败，请检查') });
        }
      }

      if (isType(this.validateHook, 'Function')) {
        data = await this.validateHook(rootStore, data, { innerValidate });
      } else {
        innerValidate();
      }

      const mode = cardStore.uiState || pageStore.status;
      let saveData = {
        params: {
          ...query,
          datasource: dataSourceAlias || cardStore?.dataSource || '',
          billnum: options.billNo || params.billNo,
          billId: options.billId || params.billId,
          busiObj: options.busiObj || query.busiObj,
          mode
        },
        data: { data }
      };
      // 复制单据场景
      if (params.initiator === 'COPY') {
        Object.assign(saveData.params, { initiator: params.initiator });
      }
      // beforeSaveHook
      if (isType(this.beforeSaveHook, 'Function')) {
        saveData = await this.beforeSaveHook(rootStore, saveData);
      }
      // 发送请求
      let saveRes = await services.save(saveData);
      // afterSaveHook
      if (isType(this.afterSaveHook, 'Function')) {
        saveRes = await this.afterSaveHook(rootStore, saveRes);
      }
      // 重置状态
      delete params.initiator;
      // 纠正billId、code
      const saveResData = saveRes.data;
      Object.assign(params, { billId: saveResData.id, code: saveResData.code });

      // 数据回写
      cardStore.setValue(saveRes.data, { merge: true });

      if (targetStore.storeType === 'CardStore') {
        cardStore.setUiState(PageStatusType.BROWSE);
        if (!treeStore) {
          if (mode == PageStatusType.ADD) {
            if (isGoBackAfterSave !== false) {
              goBack.doAction.call(this, rootStore, { prevId: saveResData.id });
            }
          }
        }
      } else {
        targetStore.setUiState(cardStore.uiState);
      }
      utils.message.success(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8002E', '保存成功', undefined, { returnStr: true }));
      return saveRes;
    } catch (err: any) {
      utils.message.danger(err?.message);
      return Promise.reject(err);
    } finally {
      pageStore?.endLoading?.();
    }
  }
}

/**
 * 保存/新增-行操作
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  tableStoreKey: string， 默认值：tableStore
 *  dataSourceAlias: string
 *  // 选填字段
 *  billNo: string， 默认值：params.billNo
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeSaveHook: 保存请求前钩子
 *  afterSaveHook: 保存请求后钩子
 *  validateHook: 校验钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const batchSaveByRow = {
  async doAction(rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['tableStoreKey', 'dataSourceAlias']);
    const { dataSourceAlias = '' } = options;
    const { utils = {}, pageStore = {} } = rootStore;
    const { params = {}, query = {} } = pageStore;
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    const mode = hoverRow?.uiState || tableStore.uiState || pageStore.status;

    try {
      pageStore?.startLoading?.();
      let data = []; const changedRows = tableStore.getChangedRows();
      if (hoverRow) { // 直接执行当前行上的按钮操作
        data = [hoverRow.dataEntityToJson()]
      } else {
        data = changedRows.map((v: any) => v.dataEntityToJson());
      }
      if (data.length === 0) {
        utils.message.warning(lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE05900021', '没有检测到数据更新需要保存'));
        return Promise.reject(lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE05900021', '没有检测到数据更新需要保存'));
      }

      // 数据校验
      const innerValidate = () => {
        (hoverRow ? [hoverRow] : changedRows).forEach((rowStore: any) => {
          if (rowStore.getValue('_status') !== 'Delete') {
            const validateRes = rowStore.validate();
            if (validateRes.hasErr) {
              this.abort(`${lang.localeUuid('UID:P_CORE-YNF-FE_189530CC05B80003', { num: rowStore.order + 1 })}${validateRes.errMsgs}`);
            }
          }
        })
      }
      if (isType(this.validateHook, 'Function')) {
        data = await this.validateHook(rootStore, data, { innerValidate });
      } else {
        innerValidate();
      }

      const isAsync = false; // data.length > 1

      const saveRes: any = await batchDo.call(this, rootStore, {
        ...options,
        dataSourceAlias,
        action: 'save',
        title: lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE05900020', '保存'),
        isAsync,
        mode,
        data
      });

      // 数据回写
      if (hoverRow) {
        if (tableStore.uiState === PageStatusType.BROWSE) {
          hoverRow.setReadOnly(true);
        }
        Object.entries(saveRes.data).forEach(([key, value]) => hoverRow.setValue(key, value));
        hoverRow.set_Status('Unchanged');
      } else {
        tableStore.setUiState(PageStatusType.BROWSE);
        if (!isAsync) {
          changedRows.forEach((row: any, index: number) => {
            if (row.getValue('_status') === 'Delete') {
              tableStore.deleteRow(row);
            } else {
              const rowData = saveRes.data?.infos?.[index] ?? saveRes.data;
              Object.entries(rowData).forEach(([key, value]) => row.setValue(key, value));
              row.set_Status('Unchanged');
            }
          });
        }
      }
      utils.message.success(lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8002E', '保存成功', undefined, { returnStr: true }));
      return saveRes;
    } catch (err: any) {
      utils.message.danger(err?.message);
      return Promise.reject(err);
    } finally {
      pageStore?.endLoading?.();
    }
  }
}

/**
 * 保存并新增
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 选填字段
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeSaveHook: 保存请求前钩子
 *  afterSaveHook: 保存请求后钩子
 *  validateHook: 校验钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const saveAndAdd = {
  get visible() {
    const cardStoreKey = this.params?.cardStoreKey || 'cardStore'
    const cardStore = this.rootStore.get(cardStoreKey)
    if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
    return cardStore.isEdit
  },
  get enable() {
    return true;
  },
  beforeSaveHook: save.beforeSaveHook,
  async doAction(rootStore: any, options: any = {}) {
    const { utils = {}, pageStore = {} } = rootStore;
    try {
      // pageStore?.startLoading?.();
      // save 请求
      await save.doAction.call(this, rootStore, { ...options, isGoBackAfterSave: false });
      // add 请求
      const addRes = await add.apply(this, arguments);
      return addRes;
    } catch (err: any) {
      utils.message.danger(err?.message);
      return Promise.reject(err);
    } finally {
      // pageStore?.endLoading?.();
    }
  }
}

/**
 * 保存并提交
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  cardStoreKey: string， 默认值：cardStore
 *  // 选填字段
 *  dataSourceAlias: string， 默认值：cardStore.dataSource
 *  billNo: string， 默认值：params.billNo
 *  billId: string， 默认值：params.billId
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  beforeSaveHook: 保存请求前钩子
 *  afterSaveHook: 保存请求后钩子
 *  validateHook: 校验钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function saveAndSubmit(rootStore: any, options: any = {}) {
  const { utils = {}, pageStore = {} } = rootStore;
  try {
    // pageStore?.startLoading?.();
    // save 请求
    const saveRes = await save.doAction.call(this, rootStore, { ...options, isGoBackAfterSave: false });
    // submit 请求
    const submitRes = await submit.doAction.call(this, rootStore, { ...options, billId: saveRes.data.id });
    return submitRes;
  } catch (err: any) {
    utils.message.danger(err?.message);
    return Promise.reject(err);
  } finally {
    // pageStore?.endLoading?.();
  }
}
